import axios from "./axios";

/**
 * 获取待处理行动项的数量
 * @param {Object} params - 筛选参数
 * @param {string} params.status - 状态筛选，如'pending_manual_review'
 * @param {string} params.priority - 优先级筛选，如'high'
 * @param {string} params.source_type - 来源类型筛选
 * @returns {Promise<number>} 待处理行动项数量
 */
export const getActionItemsCount = async (params = { status: "pending_manual_review" }) => {
    try {
        const response = await axios.get("/v1/action_items/count", { params });
        return response.data.count || 0;
    } catch (error) {
        console.error("获取待处理行动项数量失败:", error);
        return 0;
    }
};

/**
 * 获取行动项列表
 * @param {Object} params - 筛选和分页参数
 * @param {string} params.status - 状态筛选，支持逗号分隔的多个状态
 * @param {number} params.page - 当前页码，从1开始
 * @param {number} params.page_size - 每页数量
 * @param {string} params.sort_by - 排序字段
 * @param {string} params.order - 排序方向
 * @param {string} params.source_type - 来源类型筛选
 * @param {string} params.action_type - 建议操作类型筛选
 * @param {string} params.search_key - 搜索关键词
 * @param {string} params.created_after - 创建时间晚于此时间点
 * @param {string} params.created_before - 创建时间早于此时间点
 * @param {number} params.priority - 优先级筛选
 * @param {number} params.flight_operation_id - 关联航班ID
 * @param {string} params.resolution_status - 解决状态
 * @returns {Promise<Object>} 包含分页信息和行动项列表的对象
 */
export const getActionItems = async (
    params = {
        status: "pending_manual_review",
        page: 1,
        page_size: 20,
        sort_by: "created_at",
        order: "desc",
    },
) => {
    try {
        const response = await axios.get("/v1/action_items", { params });

        // 后端现在返回标准的PaginatedResponse格式
        return {
            data: {
                items: response.data.items || [],
                total: response.data.total || 0,
            },
        };
    } catch (error) {
        console.error("获取行动项列表失败:", error);
        throw error;
    }
};

/**
 * 获取单个行动项详情
 * @param {number} id - 行动项ID
 * @returns {Promise<Object>} 行动项详情
 */
export const getActionItemById = async (id) => {
    try {
        const response = await axios.get(`/v1/action_items/${id}`);
        return response.data;
    } catch (error) {
        console.error(`获取行动项 ${id} 详情失败:`, error);
        throw error;
    }
};

/**
 * 更新行动项的 user_modified_payload
 * @param {number} id - 行动项ID
 * @param {Object} data - 更新数据
 * @param {Object} data.user_modified_payload - 用户修改后的载荷
 * @returns {Promise<Object>} 更新结果
 */
export const updateActionItem = async (id, data) => {
    try {
        const response = await axios.patch(`/v1/action_items/${id}`, data);
        return response.data;
    } catch (error) {
        console.error(`更新行动项 ${id} 失败:`, error);
        throw error;
    }
};

/**
 * 处理行动项
 * @param {number} id - 行动项ID
 * @param {Object} data - 处理数据
 * @param {string} data.action_taken - 采取的操作，如 'USER_CONFIRMED', 'USER_MODIFIED_CONFIRMED', 'USER_REJECTED'
 * @param {Object} data.user_modified_payload - 用户修改后的载荷（当action_taken为'USER_MODIFIED_CONFIRMED'时需要）
 * @param {string} data.user_remarks - 用户备注
 * @returns {Promise<Object>} 处理结果
 */
export const processActionItem = async (id, data) => {
    try {
        const response = await axios.post(`/v1/action_items/${id}/process`, {
            ...data,
        });
        return response.data;
    } catch (error) {
        console.error(`处理行动项 ${id} 失败:`, error);
        throw error;
    }
};
